import React, { useRef, useState } from 'react';
import { FaUpload, FaTimes, FaFileAlt } from 'react-icons/fa';

const formatSize = (bytes) => {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Themed replacement for Cloudscape's FileUpload.
 *
 * Keeps the same controlled contract the call sites already use — `value` is
 * an array of File objects and `onChange` receives `{ detail: { value } }` —
 * so swapping it in doesn't touch the surrounding upload logic.
 */
export default function FileUpload({
  value = [],
  onChange,
  multiple = false,
  accept,
  buttonText,
  hint,
  disabled = false,
}) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const emit = (files) => onChange && onChange({ detail: { value: files } });

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    emit(multiple ? [...value, ...incoming] : incoming.slice(0, 1));
  };

  const removeAt = (index) => emit(value.filter((_, i) => i !== index));

  const label = buttonText || (multiple ? 'Upload files' : 'Upload file');

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) addFiles(e.dataTransfer.files);
        }}
        className={`flex flex-wrap items-center gap-3 rounded-xl border border-dashed p-3 transition-colors ${
          dragging ? 'border-cloudza-400 bg-cloudza-50' : 'border-gray-200'
        } ${disabled ? 'opacity-60' : ''}`}
      >
        <button
          type="button"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
          className="btn-outline"
        >
          <FaUpload />
          {label}
        </button>
        <span className="text-xs text-gray-400">
          {hint || 'or drag and drop here'}
        </span>

        <input
          ref={inputRef}
          type="file"
          multiple={multiple}
          accept={accept}
          disabled={disabled}
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            // Reset so selecting the same file twice still fires onChange.
            e.target.value = '';
          }}
        />
      </div>

      {value.length > 0 && (
        <ul className="mt-3 space-y-2">
          {value.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-2"
            >
              <FaFileAlt className="text-gray-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium text-gray-800 truncate">{file.name}</p>
                <p className="text-[11px] text-gray-400">
                  {formatSize(file.size)}
                  {file.lastModified ? ` · ${new Date(file.lastModified).toLocaleDateString()}` : ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => removeAt(index)}
                aria-label={`Remove ${file.name}`}
                className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <FaTimes className="text-xs" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
